import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../theme/ThemeProvider';
import { useAuth } from '../auth/AuthContext';
import { Sun, Moon, LogOut, Shield, Dumbbell, Menu, X } from 'lucide-react';
import FallingStarLogo from '../theme/FallingStarLogo';

export default function Navbar() {
  const { t, isDark, toggleTheme, glassCard, iri } = useTheme();
  const { isAuthenticated, logout, user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  const closeMobile = () => setMobileOpen(false);

  const linkStyle = (path) => ({
    fontSize: 13, fontWeight: 600, color: isActive(path) ? t.text : t.textMuted,
    padding: '8px 16px', borderRadius: 10, transition: 'all 0.2s',
    background: isActive(path) ? t.cardBg : 'transparent',
    border: isActive(path) ? `1px solid ${t.cardBorder}` : '1px solid transparent',
  });

  const mobileLinkStyle = (path) => ({
    fontSize: 15, fontWeight: 600, color: isActive(path) ? t.text : t.textMuted,
    padding: '12px 16px', borderRadius: 12, transition: 'all 0.2s', display: 'block',
    background: isActive(path) ? t.cardBg : 'transparent',
    border: isActive(path) ? `1px solid ${t.cardBorder}` : '1px solid transparent',
  });

  const btnIcon = { background: t.btnIconBg, border: `1px solid ${t.btnIconBorder}`, borderRadius: 10, color: t.textMuted, cursor: 'pointer', padding: 8, transition: 'all 0.25s', display: 'flex', alignItems: 'center', justifyContent: 'center' };

  return (
    <header style={{ position: 'sticky', top: 0, zIndex: 50, padding: '0 24px', background: t.headerBg, backdropFilter: 'blur(56px) saturate(1.5)', WebkitBackdropFilter: 'blur(56px) saturate(1.5)', borderBottom: `1px solid ${t.headerBorder}`, boxShadow: `inset 0 -1px 0 ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.3)'}, ${t.iriHeaderShadow}` }}>
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 768px) {
          .hide-mobile       { display: none !important; }
          .show-mobile-flex  { display: flex !important; }
        }
        @media (min-width: 769px) {
          .show-mobile-flex  { display: none !important; }
        }
      `}</style>
      <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
        {/* Left - Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
          <div className="iri-border" style={{ ...glassCard, width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
            <FallingStarLogo size={30} />
          </div>
          <div>
            <div className="iri-text" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>xceller.</div>
            <div style={{ fontSize: 10, color: t.textFaint, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 600 }}>Signal Automation</div>
          </div>
        </Link>

        {/* Center - Desktop Nav links */}
        <nav className="hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Link to="/" style={linkStyle('/')}>Home</Link>
          <Link to="/about" style={linkStyle('/about')}>About</Link>
          <Link to="/pricing" style={linkStyle('/pricing')}>Pricing</Link>
          {isAuthenticated && !user?.fitness_only && <Link to="/dashboard" style={linkStyle('/dashboard')}>Dashboard</Link>}
          {isAuthenticated && (user?.is_admin || user?.fitness_only) && <Link to="/fitness" style={{ ...linkStyle('/fitness'), display: 'flex', alignItems: 'center', gap: 4 }}><Dumbbell size={12} />Fitness</Link>}
          {isAuthenticated && user?.is_admin && <Link to="/admin" style={{ ...linkStyle('/admin'), display: 'flex', alignItems: 'center', gap: 4 }}><Shield size={12} />Admin</Link>}
        </nav>

        {/* Right - Desktop Actions */}
        <div className="hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={toggleTheme} style={btnIcon} title="Toggle theme">
            {isDark ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          {isAuthenticated ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Link to="/profile" style={{ fontSize: 12, color: t.textMuted, fontWeight: 600 }}>{user?.display_name}</Link>
              <button onClick={logout} style={btnIcon} title="Logout"><LogOut size={15} /></button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Link to="/login" style={{ background: iri, padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700, color: '#fff', border: 'none', boxShadow: '0 12px 28px rgba(111,168,255,0.22)' }}>
                Sign In
              </Link>
            </div>
          )}
        </div>

        {/* Mobile - Hamburger + theme toggle */}
        <div className="show-mobile-flex" style={{ display: 'none', alignItems: 'center', gap: 8 }}>
          <button onClick={toggleTheme} style={btnIcon} title="Toggle theme">
            {isDark ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          <button onClick={() => setMobileOpen(!mobileOpen)} style={btnIcon}>
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileOpen && (
        <div className="mobile-drawer" style={{
          position: 'absolute', top: 64, left: 0, right: 0, zIndex: 100,
          background: isDark
            ? 'rgba(6, 9, 24, 0.96)'
            : 'rgba(240, 242, 247, 0.96)',
          backdropFilter: 'blur(40px) saturate(1.8)',
          WebkitBackdropFilter: 'blur(40px) saturate(1.8)',
          borderBottom: `1px solid ${t.headerBorder}`,
          boxShadow: isDark
            ? '0 16px 40px rgba(0,0,0,0.5)'
            : '0 16px 40px rgba(0,0,0,0.12)',
          padding: '12px 20px 20px',
          animation: 'slideDown 0.28s cubic-bezier(0.16,1,0.3,1) both',
        }}>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Link to="/" style={mobileLinkStyle('/')} onClick={closeMobile}>Home</Link>
            <Link to="/about" style={mobileLinkStyle('/about')} onClick={closeMobile}>About</Link>
            <Link to="/pricing" style={mobileLinkStyle('/pricing')} onClick={closeMobile}>Pricing</Link>
            {isAuthenticated && !user?.fitness_only && <Link to="/dashboard" style={mobileLinkStyle('/dashboard')} onClick={closeMobile}>Dashboard</Link>}
            {isAuthenticated && (user?.is_admin || user?.fitness_only) && (
              <Link to="/fitness" style={{ ...mobileLinkStyle('/fitness'), display: 'flex', alignItems: 'center', gap: 6 }} onClick={closeMobile}><Dumbbell size={14} />Fitness</Link>
            )}
            {isAuthenticated && user?.is_admin && (
              <Link to="/admin" style={{ ...mobileLinkStyle('/admin'), display: 'flex', alignItems: 'center', gap: 6 }} onClick={closeMobile}><Shield size={14} />Admin</Link>
            )}
          </nav>
          <div style={{ height: 1, background: t.divider, margin: '12px 0' }} />
          {isAuthenticated ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Link to="/profile" style={{ fontSize: 14, color: t.textMuted, fontWeight: 600, padding: '8px 16px' }} onClick={closeMobile}>{user?.display_name}</Link>
              <button onClick={() => { logout(); closeMobile(); }} style={{ ...btnIcon, padding: '8px 14px', gap: 6, fontSize: 12, fontWeight: 600 }}><LogOut size={14} /> Logout</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, padding: '4px 0' }}>
              <Link to="/login" onClick={closeMobile} style={{ flex: 1, textAlign: 'center', background: iri, padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700, color: '#fff', boxShadow: '0 12px 28px rgba(111,168,255,0.22)' }}>
                Sign In
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
